import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Card, Button, MenuItem, TextField, IconButton,
  Table, TableBody, TableCell, TableHead, TableRow, Paper, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, Alert,
  Tooltip, CircularProgress
} from '@mui/material';
import { Add, Delete, Edit, Event, BeachAccess } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import { clientsAPI, holidaysAPI } from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import DevBadge from '../../components/common/DevBadge';

dayjs.locale('es');

const MONTHS = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

// ─── Colombian public holidays auto-seed helper ───────────────────────────
// Returns the list of Colombian national public holidays for a given year
function colombianHolidays(year) {
  // Fixed holidays
  const fixed = [
    { month: 1,  day: 1,  name: 'Año Nuevo' },
    { month: 5,  day: 1,  name: 'Día del Trabajo' },
    { month: 7,  day: 20, name: 'Día de la Independencia' },
    { month: 8,  day: 7,  name: 'Batalla de Boyacá' },
    { month: 12, day: 8,  name: 'Inmaculada Concepción' },
    { month: 12, day: 25, name: 'Navidad' },
  ];
  // Easter-based (variable)
  // puente:true → se traslada al lunes siguiente si no cae en lunes (Ley 51/1983)
  const easter = getEasterDate(year);
  const easterBased = [
    { offset: -3,  name: 'Jueves Santo',             puente: false },
    { offset: -2,  name: 'Viernes Santo',             puente: false },
    { offset: 39,  name: 'Ascensión del Señor',       puente: true  },
    { offset: 60,  name: 'Corpus Christi',            puente: true  },
    { offset: 68,  name: 'Sagrado Corazón de Jesús',  puente: true  },
  ];
  // "Puente" holidays (moved to next Monday if not already Mon)
  const puente = [
    { month: 1,  day: 6,  name: 'Día de Reyes' },
    { month: 3,  day: 19, name: 'San José' },
    { month: 6,  day: 29, name: 'San Pedro y San Pablo' },
    { month: 8,  day: 15, name: 'Asunción de la Virgen' },
    { month: 10, day: 12, name: 'Día de la Raza' },
    { month: 11, day: 1,  name: 'Día de Todos los Santos' },
    { month: 11, day: 11, name: 'Independencia de Cartagena' },
  ];

  const result = [];

  fixed.forEach(({ month, day, name }) => {
    result.push({ date: `${year}-${String(month).padStart(2,'0')}-${String(day).padStart(2,'0')}`, name });
  });

  easterBased.forEach(({ offset, name, puente: isPuente }) => {
    const d = new Date(easter);
    d.setUTCDate(d.getUTCDate() + offset);
    if (isPuente) {
      const dow = d.getUTCDay(); // 0=Sun, 1=Mon
      if (dow !== 1) {
        const daysToAdd = dow === 0 ? 1 : (8 - dow);
        d.setUTCDate(d.getUTCDate() + daysToAdd);
      }
    }
    result.push({ date: d.toISOString().slice(0,10), name });
  });

  puente.forEach(({ month, day, name }) => {
    const d = new Date(Date.UTC(year, month - 1, day));
    const dow = d.getUTCDay(); // 0=Sun, 1=Mon
    if (dow !== 1) {
      const daysToAdd = dow === 0 ? 1 : (8 - dow);
      d.setUTCDate(d.getUTCDate() + daysToAdd);
    }
    result.push({ date: d.toISOString().slice(0,10), name });
  });

  return result.sort((a, b) => a.date.localeCompare(b.date));
}

// Tondering algorithm for Easter Sunday
function getEasterDate(year) {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(Date.UTC(year, month - 1, day));
}

export default function HolidaysManagement() {
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initLoading, setInitLoading] = useState(true);

  // Dialog state
  const [dialog, setDialog] = useState({ open: false, mode: 'create', item: null });
  const [form, setForm] = useState({ date: null, name: '' });
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  // Confirm delete
  const [confirmDelete, setConfirmDelete] = useState({ open: false, id: null, name: '' });

  // Seed dialog
  const [seedDialog, setSeedDialog] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [seedResult, setSeedResult] = useState(null);

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - 1 + i);

  // Load clients once
  useEffect(() => {
    clientsAPI.getAll()
      .then(res => {
        setClients(res.data || []);
        if (res.data?.length > 0) setSelectedClient(res.data[0]._id);
      })
      .catch(console.error)
      .finally(() => setInitLoading(false));
  }, []);

  // Load holidays when client or year changes
  const loadHolidays = useCallback(async () => {
    if (!selectedClient) return;
    setLoading(true);
    try {
      const res = await holidaysAPI.getAll({
        clientId: selectedClient,
        year: selectedYear
      });
      setHolidays(res.data || []);
    } catch { }
    finally { setLoading(false); }
  }, [selectedClient, selectedYear]);

  useEffect(() => { loadHolidays(); }, [loadHolidays]);

  // ─── Grouped by month ───────────────────────────────────────────────────
  const grouped = React.useMemo(() => {
    const map = {};
    holidays.forEach(h => {
      const month = parseInt(h.date.slice(5, 7), 10) - 1; // 0-based
      if (!map[month]) map[month] = [];
      map[month].push(h);
    });
    return map;
  }, [holidays]);

  // ─── Handle create/edit dialog ──────────────────────────────────────────
  const openCreate = () => {
    setForm({ date: dayjs(), name: '' });
    setFormError('');
    setDialog({ open: true, mode: 'create', item: null });
  };

  const openEdit = (item) => {
    setForm({ date: dayjs(item.date.slice(0, 10)), name: item.name });
    setFormError('');
    setDialog({ open: true, mode: 'edit', item });
  };

  const closeDialog = () => setDialog({ open: false, mode: 'create', item: null });

  const handleSave = async () => {
    if (!form.date || !form.name.trim()) {
      setFormError('Fecha y nombre son requeridos');
      return;
    }
    setSaving(true); setFormError('');
    try {
      if (dialog.mode === 'create') {
        await holidaysAPI.create({
          clientId: selectedClient,
          date: form.date.format('YYYY-MM-DD'),
          name: form.name.trim()
        });
      } else {
        await holidaysAPI.update(dialog.item._id, { name: form.name.trim() });
      }
      await loadHolidays();
      closeDialog();
    } catch (err) {
      setFormError(err.response?.data?.error || 'Error al guardar');
    } finally { setSaving(false); }
  };

  // ─── Handle delete ────────────────────────────────────────────────────
  const handleDelete = async () => {
    try {
      await holidaysAPI.delete(confirmDelete.id);
      setHolidays(prev => prev.filter(h => h._id !== confirmDelete.id));
    } catch { }
    setConfirmDelete({ open: false, id: null, name: '' });
  };

  // ─── Seed national holidays ───────────────────────────────────────────
  const handleSeed = async () => {
    setSeeding(true); setSeedResult(null);
    const suggestions = colombianHolidays(selectedYear);
    let created = 0; let skipped = 0;
    for (const s of suggestions) {
      try {
        await holidaysAPI.create({ clientId: selectedClient, date: s.date, name: s.name });
        created++;
      } catch (err) {
        if (err.response?.status === 409) skipped++; // already exists
      }
    }
    setSeedResult({ created, skipped });
    await loadHolidays();
    setSeeding(false);
  };

  if (initLoading) return <LoadingSpinner />;

  const selectedClientName = clients.find(c => c._id === selectedClient)?.name || '';

  return (
    <Box>
      <DevBadge id="page:admin-holidays" />
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3} flexWrap="wrap" gap={2}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Días Festivos</Typography>
          <Typography variant="body2" color="text.secondary">
            Configuración de festivos por cliente — afecta las horas esperadas de la semana
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={openCreate}
          disabled={!selectedClient}
          sx={{ textTransform: 'none' }}
        >
          Agregar festivo
        </Button>
      </Box>

      {/* Filters */}
      <Card sx={{ p: 2, mb: 3 }}>
        <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
          <TextField
            select
            label="Cliente"
            value={selectedClient}
            onChange={e => setSelectedClient(e.target.value)}
            sx={{ minWidth: 220 }}
            size="small"
          >
            {clients.map(c => <MenuItem key={c._id} value={c._id}>{c.name}</MenuItem>)}
          </TextField>
          <TextField
            select
            label="Año"
            value={selectedYear}
            onChange={e => setSelectedYear(Number(e.target.value))}
            sx={{ minWidth: 110 }}
            size="small"
          >
            {years.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
          </TextField>
          <Box flex={1} />
          <Button
            variant="outlined"
            startIcon={<BeachAccess />}
            onClick={() => { setSeedResult(null); setSeedDialog(true); }}
            disabled={!selectedClient}
            sx={{ textTransform: 'none' }}
            size="small"
          >
            Cargar festivos nacionales Colombia
          </Button>
        </Box>
      </Card>

      {/* Summary chip */}
      {!loading && (
        <Box display="flex" gap={1} mb={2} flexWrap="wrap">
          <Chip
            icon={<Event />}
            label={`${holidays.length} festivo${holidays.length !== 1 ? 's' : ''} en ${selectedYear}`}
            variant="outlined"
            color={holidays.length > 0 ? 'primary' : 'default'}
            size="small"
          />
          {holidays.length > 0 && (
            <Chip
              label={`−${holidays.length * 8}h del año laboral`}
              size="small"
              variant="outlined"
              color="warning"
            />
          )}
        </Box>
      )}

      {/* Holidays grouped by month */}
      {loading ? (
        <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
      ) : holidays.length === 0 ? (
        <Card sx={{ p: 4, textAlign: 'center' }}>
          <Event sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography color="text.secondary">
            No hay festivos configurados para{' '}
            <strong>{selectedClientName}</strong> en {selectedYear}
          </Typography>
          <Typography variant="caption" color="text.disabled">
            Usa "Cargar festivos nacionales Colombia" para agregar los festivos automáticamente
          </Typography>
        </Card>
      ) : (
        Object.keys(grouped).sort((a, b) => a - b).map(monthIdx => (
          <Box key={monthIdx} mb={3}>
            <Typography variant="subtitle2" fontWeight={600} color="primary" mb={1}>
              {MONTHS[monthIdx]}
            </Typography>
            <Paper variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell width={130}><strong>Fecha</strong></TableCell>
                    <TableCell><strong>Nombre / descripción</strong></TableCell>
                    <TableCell width={60} align="center"><strong>Día</strong></TableCell>
                    <TableCell width={100} align="right"><strong>Acciones</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {grouped[monthIdx].map(h => {
                    const d = dayjs(h.date.slice(0, 10));
                    return (
                      <TableRow key={h._id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>
                            {d.format('DD [de] MMMM')}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{h.name}</Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                            {d.format('ddd')}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Editar nombre">
                            <IconButton size="small" onClick={() => openEdit(h)}>
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Eliminar festivo">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => setConfirmDelete({ open: true, id: h._id, name: h.name })}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Paper>
          </Box>
        ))
      )}

      {/* Add / Edit dialog */}
      <Dialog open={dialog.open} onClose={closeDialog} maxWidth="xs" fullWidth>
        <DialogTitle>
          {dialog.mode === 'create' ? 'Agregar festivo' : 'Editar festivo'}
        </DialogTitle>
        <DialogContent>
          {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <DatePicker
              label="Fecha del festivo"
              value={form.date}
              onChange={d => setForm(f => ({ ...f, date: d }))}
              disabled={dialog.mode === 'edit'}
              slotProps={{ textField: { fullWidth: true, size: 'small' } }}
            />
            <TextField
              label="Nombre / descripción"
              value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              fullWidth
              size="small"
              placeholder="Ej. Día de la Independencia"
              inputProps={{ maxLength: 100 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} sx={{ textTransform: 'none' }}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            sx={{ textTransform: 'none' }}
          >
            {saving ? 'Guardando…' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Seed national holidays dialog */}
      <Dialog open={seedDialog} onClose={() => { if (!seeding) { setSeedDialog(false); setSeedResult(null); } }} maxWidth="xs" fullWidth>
        <DialogTitle>Cargar festivos nacionales Colombia {selectedYear}</DialogTitle>
        <DialogContent>
          {seedResult ? (
            <Alert severity="success" sx={{ mt: 1 }}>
              <strong>{seedResult.created}</strong> festivo{seedResult.created !== 1 ? 's' : ''} agregado{seedResult.created !== 1 ? 's' : ''}.
              {seedResult.skipped > 0 && ` ${seedResult.skipped} ya existía${seedResult.skipped !== 1 ? 'n' : ''} y fueron omitido${seedResult.skipped !== 1 ? 's' : ''}.`}
            </Alert>
          ) : (
            <Typography variant="body2" color="text.secondary" mt={1}>
              Se cargarán automáticamente los festivos nacionales de Colombia para{' '}
              <strong>{selectedYear}</strong> en el cliente <strong>{selectedClientName}</strong>.
              Los festivos que ya existan no serán duplicados.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => { setSeedDialog(false); setSeedResult(null); }}
            disabled={seeding}
            sx={{ textTransform: 'none' }}
          >
            {seedResult ? 'Cerrar' : 'Cancelar'}
          </Button>
          {!seedResult && (
            <Button
              variant="contained"
              onClick={handleSeed}
              disabled={seeding}
              sx={{ textTransform: 'none' }}
            >
              {seeding ? 'Cargando…' : 'Cargar festivos'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Confirm delete */}
      <ConfirmDialog
        open={confirmDelete.open}
        title="Eliminar festivo"
        message={`¿Eliminar "${confirmDelete.name}"? Esta acción afectará el cálculo de horas esperadas.`}
        onConfirm={handleDelete}
        onCancel={() => setConfirmDelete({ open: false, id: null, name: '' })}
      />
    </Box>
  );
}
